<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<div class="d-flex flex-row justify-content-start align-items-center">
  @if (readOnly) {
    <mat-icon
      class="mdi-24px"
      fontIcon="mdi-alert-octagon"
      matTooltip="Read-only because the current file is not in edit mode or you do not have edit permissions"
      matTooltipShowDelay="0"
    ></mat-icon>
  }
  @if (promptForReload) {
    <button
      mat-icon-button
      matTooltip="Reload this module to view changes. Unsaved changes will be lost."
      matTooltipShowDelay="0"
      (click)="reload()"
      >
      <mat-icon class="mdi-24px" fontIcon="mdi-alert-circle"> </mat-icon>
    </button>
  }
  <h1 [ngClass]="promptForReload ? 'alert' : ''">
    {{ selectedModule.name }}
  </h1>
</div>
<div style="margin-top: -20px">{{ selectedModule.path }}</div>
<div style="margin-bottom: 20px">{{ selectedModule.description }}</div>
<form [formGroup]="form" (ngSubmit)="submit()">
  <mat-form-field>
    <mat-label>Version</mat-label>
    <mat-select
      formControlName="selectedVersion"
      (selectionChange)="onSelectedVersionChanged($event.value)"
      >
      @for (version of selectedModule.versions; track version) {
        <mat-option
          [value]="version"
          >
          {{ version.name }}
        </mat-option>
      }
    </mat-select>
  </mat-form-field>

  <div class="flex-grow-1 d-flex flex-column" style="overflow-y: scroll; height: 400px">
    <mat-card appearance="outlined">
      <mat-form-field>
        <mat-label [ngClass]="nameChanged ? 'changed-hint' : ''"
          >Name of this instance</mat-label
          >
          <input
            matInput
            placeholder="Name of this module instance"
            formControlName="newName"
            />
          @if (nameChanged) {
            <mat-hint
              align="end"
              class="changed-hint"
              [matTooltip]="'Click to accept new name:\n' + newName"
              [matTooltipShowDelay]="0"
              (click)="acceptName()"
              >Changed</mat-hint
              >
            }
          </mat-form-field>
        </mat-card>
        <ng-container formGroupName="values">
          @for (moduleField of moduleFields; track moduleField) {
            <mat-card appearance="outlined">
              <mat-form-field>
                @if (!moduleField.isOptional) {
                  <mat-hint>Required</mat-hint>
                }
                @if (moduleField.changed) {
                  <mat-hint
                    align="end"
                    class="changed-hint"
                    [matTooltip]="'Click to accept new value:\n' + moduleField.value"
                    [matTooltipShowDelay]="0"
                    (click)="acceptValue(moduleField)"
                    >Changed</mat-hint
                    >
                  }
                  <mat-label [ngClass]="moduleField.changed ? 'changed-hint' : ''">{{
                    moduleField.name
                  }}</mat-label>
                  @if (!moduleField.isMultiLine()) {
                    <input
                      matInput
                      placeholder="{{ moduleField.name }}"
                      matTooltip="{{ moduleField.description }}"
                      formControlName="{{ moduleField.name }}"
                      />
                  } @else {
                    <textarea
                      matInput
                      placeholder="{{ moduleField.name }}"
                      matTooltip="{{ moduleField.description }}"
                      cdkTextareaAutosize
                      #autosize="cdkTextareaAutosize"
                      formControlName="{{ moduleField.name }}"
                      spellcheck="false"
                    ></textarea>
                  }
                  @if (variables?.length > 0 || outputs?.length > 0) {
                    <button
                      type="button"
                      color="primary"
                      mat-icon-button
            (click)="
              trigger.panelOpen ? trigger.closePanel() : trigger.openPanel()
            "
                      matTooltip="View variables and outputs"
                      matSuffix
                      >
                      <mat-icon class="mdi-16px" fontIcon="mdi-text"> </mat-icon>
                    </button>
                  }
                  <input
                    class="hidden"
                    [matAutocomplete]="autoGroup"
                    #trigger="matAutocompleteTrigger"
                    />
                  <mat-autocomplete
                    #autoGroup
                    (optionSelected)="autocompleteSelected(moduleField.name, $event)"
                    >
                    @if (variables?.length > 0) {
                      <mat-optgroup label="Variables">
                        @for (variable of variables; track variable) {
                          <mat-option
                            [value]="variable.terraform"
                            >
                            {{ variable.name }}
                          </mat-option>
                        }
                      </mat-optgroup>
                    }
                    @if (outputs?.length > 0) {
                      <mat-optgroup label="Outputs">
                        @for (output of outputs; track output) {
                          <mat-option
                            [value]="output.terraform"
                            >
                            {{ output.designModuleName }}.{{ output.name }}
                          </mat-option>
                        }
                      </mat-optgroup>
                    }
                  </mat-autocomplete>
                </mat-form-field>
              </mat-card>
            }
          </ng-container>
        </div>

        <div class="form-actions d-flex flex-row gap-3">
          <button
            mat-raised-button
            color="primary"
            type="submit"
            [disabled]="readOnly || !form.valid || !form.dirty || isSaving"
            tabindex="1"
            >
            <span [matTooltip]="readOnly ? 'Can only submit in edit mode' : null">
              {{ isSaving ? 'Saving...' : submitButtonText }}
            </span>
          </button>
          <button
            type="button"
            mat-raised-button
            color="primary"
            (click)="cancel()"
            tabIndex="2"
            >
            @if (!form.dirty) {
              Close
            }
            @if (form.dirty) {
              Cancel
            }
          </button>
        </div>
      </form>
